import React, { ReactNode } from "react"
import { ApolloClient, InMemoryCache, ApolloLink, HttpLink } from "@apollo/client"
import { ApolloProvider } from "@apollo/client/react"
import { ErrorLink } from "@apollo/client/link/error"
import { SetContextLink } from "@apollo/client/link/context"
import { CombinedGraphQLErrors } from "@apollo/client/errors"
import { getMainDefinition } from "@apollo/client/utilities"
import merge from "lodash/merge"
import { useConfig } from "../../configuration/useConfig"
import { fetchAuthSession } from "aws-amplify/auth"
import { RemoveTypenameFromVariablesLink } from "@apollo/client/link/remove-typename"
import { AwsRumClient } from "helpers/awsRumClient"
import { isConnectivityError } from "helpers/isConnectivityError"
import { amplifySubscriptionLink } from "./amplifySubscriptionLink"

type Props = {
  children: ReactNode
}

// Records an error to RUM enriched with the GraphQL operation name, current
// route and variables — shared by the GraphQL- and network-error branches of
// the error link below.
const recordRumError = (
  rumClient: ReturnType<typeof AwsRumClient>,
  message: string,
  name: string,
  operation: { operationName?: string | null; variables?: unknown }
): void => {
  const operationName = operation?.operationName ?? "unknown"
  const errorObj = new Error([message, `Operation: ${operationName}`].join(" | "))
  errorObj.name = name
  errorObj.stack = `Route: ${window.location.pathname} | Variables: ${JSON.stringify(operation.variables)}`
  rumClient?.recordError(errorObj)
}

const ApolloClientProvider = ({ children }: Props) => {
  const { awsRegion, graphqlApiUrl, rumAppMonitorId, rumIdentityPool } = useConfig().config
  const url: string = graphqlApiUrl

  const getAccessToken = async (): Promise<string> =>
    (await fetchAuthSession().then((res) =>
      res.tokens?.accessToken.toString()
    )) ?? ""

  const parseCustomerAttribute = (value: string | object): object => {
    switch (typeof value) {
      case "string":
        return parseCustomerAttribute(JSON.parse(value))
      case "object":
        return value
      default:
        return {}
    }
  }

  const removeTypenameLink = new RemoveTypenameFromVariablesLink()

  const httpLink = new HttpLink({ uri: url })

  const authLink = new SetContextLink(async (prevContext) => ({
    headers: {
      ...(prevContext.headers as Record<string, string> | undefined),
      Authorization: await getAccessToken()
    }
  }))

  const client = new ApolloClient({
    link: ApolloLink.from([
      removeTypenameLink,
      new ErrorLink(({ error, operation }) => {
        const { skipRumLogging } = operation.getContext() || {};

        if (CombinedGraphQLErrors.is(error)) {
          // Handle GraphQL errors
          if (!skipRumLogging) {
            const rumClient = AwsRumClient(rumAppMonitorId, rumIdentityPool, awsRegion);
            error.errors.forEach((err) => {
              recordRumError(rumClient, err.message, "GraphQLError", operation)

              console.error(
                `[GraphQL error]: Message: ${err.message}, Location: ${JSON.stringify(
                  err.locations
                )}, Path: ${err.path}`
              )
            })
          }
        } else {
          // Handle network errors
          // Ignore AbortError - these are expected when queries are cancelled
          if (error.name === "AbortError") {
            return
          }

          // Ignore client connectivity failures (offline / DNS / network change).
          // These are not actionable, and recording them while offline triggers a
          // storm of failing cognito-identity fetches inside the RUM client.
          if (isConnectivityError(error)) {
            console.log(`[Network error - offline, skipped RUM]: ${JSON.stringify(error)}`) // eslint-disable-line
            return
          }

          const rumClient = AwsRumClient(rumAppMonitorId, rumIdentityPool, awsRegion);
          recordRumError(rumClient, error.message, error.name || "NetworkError", operation)
          const operationName = operation?.operationName ?? "unknown"
          console.log(`[Network error]: Operation: ${operationName} | ${JSON.stringify(error)}`) // eslint-disable-line
        }
      }),
      ApolloLink.split(
        ({ query }) => {
          const def = getMainDefinition(query)
          return def.kind === "OperationDefinition" && def.operation === "subscription"
        },
        amplifySubscriptionLink,
        ApolloLink.from([authLink, httpLink])
      )
    ]),
    cache: new InMemoryCache({
      typePolicies: {
        ShippingFeeModificationUpdateData: {
          keyFields: false,
        },
        OrderWorkflowRevision: {
          keyFields: ["id", "revision"],
        },
        PaymentProvider: {
          keyFields: false,
        },
        Provider: {
          keyFields: false,
        },
        QueryProductManagement: {
          merge: true
        },
        QueryOrderManagement: {
          merge: true
        },
        OrderLineModificationUpdateData: {
          keyFields: false
        },
        OrderLineModificationUpdateDataPrice: {
          keyFields: false
        },
        OrderLineModificationCreateData: {
          keyFields: false
        },
        OrderLineModificationDeleteData: {
          keyFields: false
        },
        OrderLineModification: {
          keyFields: false
        },
        OrderShippingAvardaShippingBroker: {
          merge: true
        },
        ShopperInsightsSearchHitCheckoutItem: {
          keyFields: false,
        },
        CompensationPayment: {
          keyFields: false,
        },
        Order: {
          fields: {
            shippingAddress: {
              merge: true
            },
            customerAttribute: {
              merge(existing, incoming) {
                return merge({}, parseCustomerAttribute(existing), parseCustomerAttribute(incoming))
              }
            },
          }
        }
      }
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: false
      }
    }
  })

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

export { ApolloClientProvider }
